import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Theme = 'light' | 'dark';

interface ThemeContextValue {
  readonly theme: Theme;
  readonly setTheme: (theme: Theme) => void;
  readonly toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme precisa estar dentro de <ThemeProvider>.');
  return ctx;
}

const THEME_COOKIE = 'qc_theme';

function readThemeCookie(): Theme | null {
  if (typeof document === 'undefined') return null;
  const match = /(?:^|;\s*)qc_theme=(light|dark)/.exec(document.cookie);
  return (match?.[1] as Theme | undefined) ?? null;
}

function systemTheme(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Tema claro/escuro.
 *
 * A preferência vai para um cookie NÃO sensível — e não para `localStorage` —
 * porque o projeto mantém uma única política de persistência no cliente: nada
 * de storage do browser. O tema inicial já é aplicado pelo script inline do
 * `index.html`, antes da primeira pintura; aqui apenas o mantemos sincronizado.
 *
 * Ambos os temas foram construídos com contraste >= 4.5:1 para texto normal
 * (ver `styles/tokens.css`) — o modo escuro não é uma versão degradada.
 */
export function ThemeProvider({ children }: { readonly children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readThemeCookie() ?? systemTheme());

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  // Segue a preferência do sistema enquanto o usuário não escolher explicitamente.
  useEffect(() => {
    if (readThemeCookie()) return;
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (event: MediaQueryListEvent) => setThemeState(event.matches ? 'dark' : 'light');
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    document.cookie = `${THEME_COOKIE}=${next}; path=/; max-age=31536000; SameSite=Strict`;
  }, []);

  const toggleTheme = useCallback(
    () => setTheme(theme === 'dark' ? 'light' : 'dark'),
    [theme, setTheme],
  );

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
